/**
 * Gemensam logik för kundprocess-steg (admin ordervy + leverantörsportal).
 */

export type OrderProcessTimelineRequest = {
  status: string;
  payment_status?: string | null;
  supplier_handles_delivery?: boolean | null;
  delivery_started_at?: string | null;
  delivery_delivered_at?: string | null;
  delivery_picked_up_at?: string | null;
  delivery_completed_at?: string | null;
};

export type OrderProcessPackingSummary = {
  status: string;
  packed_at: string | null;
  pickup_ready_email_sent_at: string | null;
} | null;

export type OrderProcessStep = { id: string; label: string; complete: boolean };

export function paymentStepComplete(request: OrderProcessTimelineRequest, balance: number): boolean {
  const ps = String(request.payment_status ?? '')
    .trim()
    .toLowerCase();
  if (ps === 'paid' || ps === 'not_required') return true;
  if (balance <= 0) return true;
  return false;
}

export function buildOrderProcessSteps(
  request: OrderProcessTimelineRequest,
  packing: OrderProcessPackingSummary,
  balance: number,
): { steps: OrderProcessStep[]; terminal: 'denied' | 'cancelled' | null } {
  const st = request.status;
  if (st === 'denied') return { steps: [], terminal: 'denied' };
  if (st === 'cancelled') return { steps: [], terminal: 'cancelled' };

  const approved = st !== 'pending';
  const paidOk = paymentStepComplete(request, balance);
  const packedAndCustomerNotified =
    Boolean(packing?.pickup_ready_email_sent_at) ||
    Boolean(packing?.packed_at) ||
    String(packing?.status ?? '') === 'packed' ||
    String(packing?.status ?? '') === 'delivered';
  const rented = st === 'rented_out' || st === 'completed';
  const completed = st === 'completed';

  const steps: OrderProcessStep[] = [
    { id: 'received', label: 'Order mottagen', complete: true },
    { id: 'approved', label: 'Godkänd för genomförande', complete: approved },
    { id: 'payment', label: 'Betalning reglerad', complete: paidOk },
  ];

  if (request.supplier_handles_delivery) {
    steps.push(
      { id: 'del_start', label: 'Leverans påbörjad', complete: Boolean(request.delivery_started_at) },
      { id: 'del_drop', label: 'Levererad till kund', complete: Boolean(request.delivery_delivered_at) },
      { id: 'del_pick', label: 'Hämtad hos kund', complete: Boolean(request.delivery_picked_up_at) },
      { id: 'del_done', label: 'Leveranslogistik klar', complete: Boolean(request.delivery_completed_at) },
    );
  }

  steps.push(
    { id: 'packed_ready', label: 'Färdigpackad', complete: packedAndCustomerNotified },
    { id: 'rented', label: 'Uthyrd / utlämnad', complete: rented },
    { id: 'done', label: 'Retur klar / avslutad', complete: completed },
  );

  return { steps, terminal: null };
}
